import { ArrowForward } from '@mui/icons-material';
import { Box, Grid, useMediaQuery } from '@mui/material';
import Button from '@mui/material/Button';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import { useTheme, styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProcessStep } from '../common/enums/enums';
import StepStopButton from '../components/StepStopButton';

const PREFIX = 'ProcessStepper';

const classes = {
  root: `${PREFIX}-root`,
  stepper: `${PREFIX}-stepper`,
  step: `${PREFIX}-step`,
  restoreButton: `${PREFIX}-restoreButton`,
  instructions: `${PREFIX}-instructions`,
  inTransit: `${PREFIX}-inTransit`,
  arrived: `${PREFIX}-arrived`,
  inProcess: `${PREFIX}-inProcess`,
  done: `${PREFIX}-done`,
  activeIcon: `${PREFIX}-activeIcon`,
  completedIcon: `${PREFIX}-completedIcon`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.root}`]: {
    width: '100%',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
    [theme.breakpoints.up('lg')]: {
      flexDirection: 'column',
    },
  },

  [`& .${classes.stepper}`]: {
    padding: theme.spacing(0),
    [theme.breakpoints.up('md')]: {
      flex: '1 0 0',
      marginRight: theme.spacing(5),
    },
    [theme.breakpoints.up('lg')]: {
      marginRight: theme.spacing(0),
    },
  },

  [`& .${classes.step}`]: {
    [theme.breakpoints.down('md')]: {
      flexDirection: 'row',
    },
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'column',
    },
  },

  [`& .${classes.restoreButton}`]: {
    color: theme.palette.success.main,
    borderColor: theme.palette.success.main,
  },

  [`& .${classes.instructions}`]: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },

  [`& .${classes.inTransit}`]: {
    color: theme.palette.action.disabled,
    '& .Mui-active': {
      color: theme.palette.info.textDark,
    },
    '& .Mui-completed': {
      color: theme.palette.action.disabled,
    },
  },

  [`& .${classes.arrived}`]: {
    color: theme.palette.action.disabled,
    '& .Mui-active': {
      color: theme.palette.primary.light,
    },
    '& .Mui-completed': {
      color: theme.palette.action.disabled,
    },
  },

  [`& .${classes.inProcess}`]: {
    color: theme.palette.action.disabled,
    '& .Mui-active': {
      color: theme.palette.warning.light,
    },
    '& .Mui-completed': {
      color: theme.palette.action.disabled,
    },
  },

  [`& .${classes.done}`]: {
    color: theme.palette.action.disabled,
    '& .Mui-active': {
      color: theme.palette.success.main,
    },
    '& .Mui-completed': {
      color: theme.palette.action.disabled,
    },
  },
}));

const GetSteps = () => {
  const { t } = useTranslation('workOrdersTable');
  return [
    {
      key: 0,
      class: 'inTransit',
      title: t('card.stepStates.intransit'),
      time: '10:30AM',
      date: '07/26/21',
      enum: ProcessStep.IN_TRANSIT,
      updatedBy: 'Tyler Johnson',
    },
    {
      key: 1,
      class: 'arrived',
      title: t('card.stepStates.arrived'),
      time: '11:15AM',
      date: '07/26/21',
      enum: ProcessStep.ARRIVED,
      updatedBy: 'Kevin Compton',
    },
    {
      key: 2,
      class: 'inProcess',
      title: t('card.stepStates.inprocess'),
      enum: ProcessStep.IN_PROCESS,
    },
    {
      key: 3,
      class: 'done',
      title: t('card.stepStates.done'),
      time: null,
      enum: ProcessStep.DONE,
    },
  ];
};

export default function ProcessStepper({
  step,
  where,
  orderId,
  isDetail = false,
}) {
  const { t } = useTranslation('workOrdersTable');
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const stepperProps = {
    orientation: isSmallScreen ? 'vertical' : 'horizontal',
  };

  const [activeStep, setActiveStep] = useState(0);
  const steps = GetSteps();
  const initialState = steps.find(state => state.enum === step);
  useEffect(() => {
    setActiveStep(initialState?.key);
  }, [setActiveStep, initialState?.key]);

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  return (
    <StyledGrid container className={classes.root}>
      <Stepper
        activeStep={activeStep}
        className={classes.stepper}
        {...stepperProps}
      >
        {steps.map(s => (
          <Step key={s?.key}>
            <StepLabel className={classes[s.class]}>
              <Grid container className={classes.step}>
                <Grid item>
                  <Box>
                    <Typography variant="subtitle2">{s.title}</Typography>
                  </Box>
                </Grid>
                {s.time ? (
                  <Grid item>
                    <Box className={classes.box}>
                      <Typography variant="caption" color="textSecondary">
                        {s.time}
                      </Typography>
                    </Box>
                  </Grid>
                ) : null}
                {isDetail && s.date ? (
                  <Grid item>
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        {s.date}
                      </Typography>
                    </Box>
                  </Grid>
                ) : null}
                {isDetail && s.updatedBy ? (
                  <Grid item>
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        {s.updatedBy}
                      </Typography>
                    </Box>
                  </Grid>
                ) : null}
              </Grid>
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      <Box mt={1}>
        {activeStep === steps.length - 1 ? (
          <Grid
            container
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
          >
            <Grid item>
              <Button
                className={classes.restoreButton}
                variant="outlined"
                size="small"
              >
                {t('card.hiddenContent.processStepButtons.restore')}
              </Button>
            </Grid>
          </Grid>
        ) : (
          <Grid
            container
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
          >
            <Grid item>
              <StepStopButton
                disabled={`${activeStep === 0 ? true : false}`}
                where={where}
                orderId={orderId}
              />
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                endIcon={<ArrowForward />}
                onClick={handleNext}
              >
                {t('card.hiddenContent.processStepButtons.next')}
              </Button>
            </Grid>
          </Grid>
        )}
      </Box>
    </StyledGrid>
  );
}
